import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import ProtectedRoute from "../components/ProtectedRoute";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { ArrowLeft, EyeOff, ShieldAlert, Lock } from "lucide-react";

// On-screen preview only.
// NOTE: absolute prevention of screenshots is not technically possible in a
// browser. We make it inconvenient (disable right-click, Ctrl+S/P/Shift+S,
// PrintScreen, selection, CSS print) and we stamp the document with the
// viewer's email + timestamp so any leaked capture is traceable.
function ContractBody() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [center, setCenter] = useState(null);
    const [industry, setIndustry] = useState(user?.industry || "");
    const [businessName, setBusinessName] = useState(user?.company_name || user?.full_name || "");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hidden, setHidden] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        api.get(`/centers/${id}`).then((r) => setCenter(r.data)).catch(() => setCenter(null));
    }, [id]);

    // Anti-capture handlers
    useEffect(() => {
        const block = (e) => {
            e.preventDefault();
            e.stopPropagation();
            toast.error("Screenshots, printing & saving are disabled for this preview.");
            return false;
        };
        const onKey = (e) => {
            const k = e.key?.toLowerCase();
            // Ctrl/Cmd + S / P
            if ((e.ctrlKey || e.metaKey) && (k === "s" || k === "p")) return block(e);
            // Ctrl/Cmd + Shift + S / P  (some browsers: save page as / screenshot)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (k === "s" || k === "p")) return block(e);
            // Windows Snipping Tool shortcut
            if (e.shiftKey && (e.metaKey || e.ctrlKey) && k === "s") return block(e);
            if (k === "printscreen") return block(e);
        };
        const onKeyUp = (e) => {
            if (e.key === "PrintScreen") {
                // Best-effort: clear the clipboard image if possible
                try { navigator.clipboard?.writeText(""); } catch (_) {}
                toast.error("Screenshots are prohibited.");
            }
        };
        const onBlur = () => setHidden(true);
        const onFocus = () => setHidden(false);
        const onVis = () => setHidden(document.visibilityState !== "visible");
        const onCopy = (e) => { e.preventDefault(); toast.error("Copying is disabled."); };
        const onContext = (e) => { e.preventDefault(); };

        document.addEventListener("keydown", onKey, true);
        document.addEventListener("keyup", onKeyUp, true);
        document.addEventListener("copy", onCopy, true);
        document.addEventListener("contextmenu", onContext, true);
        window.addEventListener("blur", onBlur);
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);

        return () => {
            document.removeEventListener("keydown", onKey, true);
            document.removeEventListener("keyup", onKeyUp, true);
            document.removeEventListener("copy", onCopy, true);
            document.removeEventListener("contextmenu", onContext, true);
            window.removeEventListener("blur", onBlur);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    // Auto-load preview when profile is already complete
    useEffect(() => {
        if (user?.industry && user?.company_name && !preview) {
            (async () => {
                try {
                    const { data } = await api.post(`/centers/${id}/contract-preview`, {
                        industry: user.industry,
                        business_name: user.company_name,
                    });
                    setPreview(data);
                } catch { /* silent */ }
            })();
        }
        // eslint-disable-next-line
    }, [user?.industry, user?.company_name, id]);

    const load = async () => {
        if (!industry.trim()) {
            toast.error("Please enter your declared line of business.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post(`/centers/${id}/contract-preview`, {
                industry: industry.trim(),
                business_name: businessName.trim(),
            });
            setPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load preview");
        } finally {
            setLoading(false);
        }
    };

    const stamp = useMemo(() => {
        const t = preview?.issued_at ? new Date(preview.issued_at) : new Date();
        return `${user?.email || ""} · ${t.toLocaleString()}`;
    }, [preview, user]);

    if (!center) return <div className="pt-32 text-center text-zinc-500 text-sm tracking-label uppercase">Loading…</div>;

    return (
        <div className="pt-20 min-h-screen bg-zinc-50 contract-page">
            <Toaster richColors position="top-center" />
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
                <Link to={`/centers/${id}`} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950">
                    <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to {center.name}
                </Link>

                <div className="mt-6 text-[10px] uppercase tracking-label text-zinc-500">Lease Contract · Preview Only</div>
                <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
                    The #1 BPLO requirement, previewed.
                </h1>
                <p className="mt-3 text-sm text-zinc-600 max-w-2xl">
                    This is the standard FWG lease contract template, filled in with your details.
                    It is shown on-screen for reference only — you cannot download, print, copy, or
                    screenshot this preview.
                </p>

                {/* Controls */}
                <div className="mt-8 border border-zinc-200 bg-white p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                        <label className="text-[10px] uppercase tracking-label text-zinc-500">Your registered business name</label>
                        <input data-testid="contract-business-name" value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                            className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:border-zinc-950 focus:outline-none text-sm" />
                    </div>
                    <div className="md:col-span-1">
                        <label className="text-[10px] uppercase tracking-label text-zinc-500">Line of business / industry</label>
                        <input data-testid="contract-industry" value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            placeholder="e.g. IT Consulting, Import/Export"
                            className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:border-zinc-950 focus:outline-none text-sm" />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                        <button data-testid="contract-preview-btn" onClick={load} disabled={loading}
                            className="w-full bg-zinc-950 text-white py-2.5 text-sm hover:bg-[#FF6A13] transition-colors disabled:opacity-40">
                            {loading ? "Loading…" : preview ? "Refresh preview" : "Show preview"}
                        </button>
                    </div>
                </div>

                {/* Warning banner */}
                <div className="mt-6 border-l-2 border-[#FF6A13] bg-orange-50 p-4 flex items-start gap-3">
                    <ShieldAlert className="h-5 w-5 text-[#FF6A13] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div className="text-xs text-zinc-800 leading-relaxed">
                        <strong>Preview-only:</strong> printing, saving, right-click, selection, and copying are disabled.
                        The content is watermarked with your email and timestamp — any captured image is traceable to this session.
                        A signed original will be issued to you by FWG when you confirm an agreement.
                    </div>
                </div>

                {/* Contract */}
                {preview ? (
                    <div
                        ref={wrapRef}
                        data-testid="contract-preview-body"
                        className="relative mt-8 bg-white border border-zinc-200 shadow-sm select-none"
                        style={{ userSelect: "none", WebkitUserSelect: "none" }}
                    >
                        {/* Diagonal BPLO DO NOT ACCEPT watermark + viewer stamp */}
                        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
                            <div className="absolute inset-0 grid grid-cols-1">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div key={i} className="flex items-center justify-center opacity-[0.08]">
                                        <div className="font-display text-5xl sm:text-7xl font-bold text-red-600 rotate-[-24deg] whitespace-nowrap tracking-tight">
                                            BPLO · DO NOT ACCEPT
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="absolute inset-0 flex flex-wrap content-around justify-around opacity-[0.09]">
                                {Array.from({ length: 18 }).map((_, i) => (
                                    <div key={i} className="text-[11px] font-mono text-zinc-700 rotate-[-24deg]">
                                        {stamp}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Blur overlay when tab not focused */}
                        {hidden && (
                            <div className="absolute inset-0 bg-white/95 backdrop-blur-xl z-10 grid place-items-center text-zinc-800">
                                <div className="text-center">
                                    <EyeOff className="h-8 w-8 mx-auto" strokeWidth={1.5} />
                                    <div className="mt-2 text-sm tracking-label uppercase">Preview hidden</div>
                                    <div className="text-xs text-zinc-500 mt-1">Return to this tab to resume.</div>
                                </div>
                            </div>
                        )}

                        <div className="relative p-6 sm:p-10">
                            <div className="text-center">
                                <div className="text-[10px] uppercase tracking-label text-zinc-500">Sample · Preview</div>
                                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-2">{preview.title}</h2>
                            </div>
                            <div className="mt-8 space-y-6 text-[13px] leading-relaxed text-zinc-800">
                                {preview.sections.map((s, i) => (
                                    <section key={i}>
                                        {s.heading && (
                                            <h3 className="font-display font-semibold text-zinc-950 mb-1">{s.heading}</h3>
                                        )}
                                        <div style={{ whiteSpace: "pre-wrap" }}>{s.body}</div>
                                    </section>
                                ))}
                            </div>
                            <div className="mt-10 pt-6 border-t border-zinc-200 text-[10px] text-zinc-500 flex flex-wrap items-center justify-between gap-2">
                                <span>Issued to: <strong className="text-zinc-800">{user?.email}</strong></span>
                                <span>Stamp: <strong className="text-zinc-800">{stamp}</strong></span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mt-8 border border-dashed border-zinc-300 bg-white p-12 text-center">
                        <Lock className="h-6 w-6 mx-auto text-zinc-500" strokeWidth={1.5} />
                        <div className="mt-3 font-display text-lg">Enter your details above to reveal the preview.</div>
                        <div className="mt-1 text-xs text-zinc-500">Only the current session can view this preview.</div>
                    </div>
                )}

                <div className="mt-8 flex items-center gap-3">
                    <button onClick={() => navigate(`/agreement/${id}`)}
                        data-testid="proceed-to-agreement-btn"
                        className="bg-[#FF6A13] text-white px-5 py-3 text-sm hover:bg-orange-600 transition-colors">
                        Proceed to agreement
                    </button>
                    <Link to={`/centers/${id}`} className="text-sm text-zinc-700 hover:text-zinc-950 underline underline-offset-4">
                        Back to center
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default function ContractPreview() {
    return (
        <ProtectedRoute roles={["client", "admin"]}>
            <ContractBody />
        </ProtectedRoute>
    );
}
